import { z } from 'zod'
import { isSpokenMedia, type MediaType } from './media'

/**
 * Accounts and sub-accounts — the tenant model the rest of the app hangs off.
 *
 * An account is a customer (or an agency). A sub-account is the level configuration and access
 * actually bind to, and every account has at least one: when it's the only one it is created
 * automatically and hidden, so nobody clicks through a level that carries no information.
 *
 * Scaffold shapes. The fields here are the ones the pages need to exist; tiering, ARR, CSM and IA
 * assignment, and product configuration all land later once the data model settles.
 */

export const accountStatusSchema = z.enum(['active', 'inactive'])

/**
 * Features an account can be entitled to — what it bought, not what its users may do.
 *
 * `subaccounts` is the first: whether this account may be split at all. Absence of an entitlement
 * means not entitled, so a plain account gets exactly the one sub-account it is created with.
 */
export const ENTITLEMENT_SUBACCOUNTS = 'subaccounts'

/**
 * Data-source entitlements — which platform sources an account may use in its topics. News is always
 * included, so only the five platforms are gated. Keyed by the platform's `TopicSource` slug; the
 * topic write path checks the mapped feature against the parent account before letting a source be
 * enabled.
 *
 * These VALUES are the canonical `source:*` feature slugs from `PRODUCT_GROUPS` below — the same rows
 * the account Products section writes — so an account entitled through Products satisfies the topic
 * gate (they must be the identical string, or the feature exists and can never be used). Note the
 * naming seams: `newsletter` (topic source) maps to `source:newsletters` (the product), and
 * `source:podcast` is carried in the catalogue for this mapping.
 */
export const DATA_SOURCE_ENTITLEMENTS = {
  x: 'source:x',
  linkedin: 'source:linkedin',
  broadcast: 'source:broadcast',
  podcast: 'source:podcast',
  newsletter: 'source:newsletters',
} as const

export type DataSourceEntitlement =
  (typeof DATA_SOURCE_ENTITLEMENTS)[keyof typeof DATA_SOURCE_ENTITLEMENTS]

/**
 * The LexisNexis licence slug — whether this account carries full article text. One spelling, so
 * the data-source catalogue below, the data API's LexisNexis switch, and the Library panel's
 * "Load full text" gate can never disagree. (The slug predates the "LexisNexis" label.)
 */
export const SOURCE_FULL_TEXT_ENTITLEMENT = 'source:full-text'

/**
 * Whether reading a hit's full body needs the LexisNexis licence. Written article text (news,
 * newsletters, anything not spoken) does; a transcript (broadcast, podcast) is a different licence
 * and stays available. An unknown media type needs it — closed, not open. The one statement of the
 * rule: the API refuses the body without the slug, and the Library panel decides whether to offer
 * it, both through this (CLI-903). It takes the stream's `MediaType`, never the backend's raw
 * article type, so a vocabulary mismatch is a type error rather than a refused transcript.
 */
export function fullTextNeedsLicence(mediaType: MediaType | null): boolean {
  return !isSpokenMedia(mediaType)
}

/**
 * Product entitlements — which delivery products a workspace's sidebar shelf may show and route to.
 * Keyed by the sidebar's product key; the VALUES are the canonical `delivery:*` slugs from
 * `PRODUCT_GROUPS`, so a product turned on in the account Products section is the same row the
 * sidebar reads. Same discipline as `DATA_SOURCE_ENTITLEMENTS`: one spelling shared by the shelf and
 * any future API gate, so a product can't exist under one name and be gated under another.
 */
export const PRODUCT_ENTITLEMENTS = {
  reports: 'delivery:reports',
  library: 'delivery:library',
  rta: 'delivery:rta',
  analytics: 'delivery:analytics',
  api: 'delivery:api',
} as const

export type ProductEntitlement = (typeof PRODUCT_ENTITLEMENTS)[keyof typeof PRODUCT_ENTITLEMENTS]

export interface ProductFeature {
  /** The entitlement row's feature slug — the wire value, never shown to anyone. */
  feature: string
  label: string
  description: string
}

export interface ProductGroup {
  id: string
  label: string
  description: string
  features: ProductFeature[]
}

/**
 * The product catalogue the account page's Products section renders — what Clipbook sells,
 * grouped the way it is sold: the data sources coverage comes from, and the delivery methods it
 * reaches the customer through. "Products" is the section's name; underneath, every toggle is an
 * `account_entitlements` row keyed by `feature`, so the wire contract and the gates
 * (`requireAccountEntitlement`) are unchanged and HubSpot can take over as the writer later.
 *
 * Deliberately data here rather than in the component: the API gates on these slugs
 * (`requireAccountEntitlement(ENTITLEMENT_SUBACCOUNTS, …)` is the model), and a slug spelled two
 * ways is a feature that exists and can never be used.
 */
export const PRODUCT_GROUPS: ProductGroup[] = [
  {
    id: 'data-sources',
    label: 'Data sources',
    description: 'Where this account’s coverage comes from.',
    features: [
      { feature: 'source:news', label: 'News', description: 'Online news coverage.' },
      {
        // The slug predates the name: `source:full-text` is the LexisNexis licence. It decides
        // whether the LexisNexis firehose feeds this account's reports at all, and rides on every
        // topic request as the data API's LexisNexis switch (legacy: lexis_nexis_full_text_enabled).
        feature: SOURCE_FULL_TEXT_ENTITLEMENT,
        label: 'LexisNexis',
        description: 'LexisNexis firehose coverage, with full article text.',
      },
      { feature: 'source:x', label: 'X', description: 'Posts from X.' },
      { feature: 'source:linkedin', label: 'LinkedIn', description: 'LinkedIn posts.' },
      {
        feature: 'source:broadcast',
        label: 'Broadcast',
        description: 'Television and radio coverage.',
      },
      { feature: 'source:podcast', label: 'Podcast', description: 'Podcast episode coverage.' },
      {
        feature: 'source:newsletters',
        label: 'Newsletters',
        description: 'Email newsletter coverage.',
      },
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery methods',
    description: 'How coverage reaches them.',
    features: [
      { feature: 'delivery:reports', label: 'Reports', description: 'Curated coverage reports.' },
      {
        // Replaced `delivery:dashboard` ("Dashboard") — Library is the row-level view of the
        // workspace's coverage stream; Analytics is the aggregate view of the SAME stream, so the
        // two are sold separately but configured once. (HubSpot still sells this as "Database" — a
        // naming seam like newsletter → source:newsletters, never shown here.)
        feature: 'delivery:library',
        label: 'Library',
        description: 'The coverage library — every article in the workspace’s stream, filterable.',
      },
      { feature: 'delivery:rta', label: 'RTA', description: 'Real-time alerts.' },
      {
        feature: 'delivery:analytics',
        label: 'Analytics',
        description: 'Coverage analytics and reporting.',
      },
      {
        // clipbook-api: REST and MCP over the workspace's endpoint. Clipbook sets the endpoint's
        // scope; the workspace manages its own keys.
        feature: 'delivery:api',
        label: 'API & MCP',
        description: 'Coverage over the Clipbook API and MCP server, with keys the client manages.',
      },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    description: 'How the account itself is structured.',
    features: [
      {
        feature: ENTITLEMENT_SUBACCOUNTS,
        label: 'Sub-accounts',
        description:
          'Split this account into separate units — an agency’s clients, or teams that should only see their own data.',
      },
    ],
  },
]

export const entitlementSchema = z.object({
  feature: z.string(),
  enabled: z.boolean(),
  /** 'manual' while a human owns this; 'hubspot' once the sync does. */
  source: z.string(),
})

export const setEntitlementSchema = z.object({
  feature: z.string().min(1).max(64),
  enabled: z.boolean(),
})

/**
 * Set (or clear) a resource's seat cap. Null lifts the cap entirely — the resource becomes
 * unlimited, which is also the default for every account until a number is set.
 */
/**
 * Give an account its own WorkOS organization.
 *
 * The prerequisite for a customer bringing their own identity provider: Directory Sync governs
 * ONE organization's membership, so a customer with an IdP cannot share Clipbook's.
 */
export const isolateAccountSchema = z.object({
  /** The organization's name in WorkOS. Defaults to the account's own name. */
  name: z.string().min(1).max(200).optional(),
  /** Email domains WorkOS should associate with it, for domain-based sign-in. */
  domains: z.array(z.string().min(3).max(253)).max(10).optional(),
})
export type IsolateAccount = z.infer<typeof isolateAccountSchema>

export const isolateAccountResponseSchema = z.object({
  accountId: z.uuid(),
  organizationId: z.string(),
})
export type IsolateAccountResponse = z.infer<typeof isolateAccountResponseSchema>

export const setSeatsSchema = z.object({
  seats: z.number().int().nonnegative().nullable(),
})
export type SetSeats = z.infer<typeof setSeatsSchema>

export const listEntitlementsResponseSchema = z.object({
  entitlements: z.array(entitlementSchema),
})

/**
 * Whose brand every report for this account wears. Lives here rather than on a template because a
 * template is shareable: two reports for one client must not disagree, and a template used by two
 * accounts must not carry the first one's colour into the second one's email.
 *
 * Colour only. The typeface used to live here too, as `sans` or `serif` — neither a real face
 * ("sans" was each reader's system font), and no account ever set anything but the default. The
 * typeface is the template's master font now (`shell.font`, CLI-793); a stored `font` is dropped
 * on parse.
 */
export const accountBrandSchema = z.object({
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb color'),
})
export type AccountBrand = z.infer<typeof accountBrandSchema>

export const accountSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: accountBrandSchema,
  status: accountStatusSchema,
  /** How many CLIENT sub-accounts it has — the non-primary ones. The account's own primary
   *  sub-account isn't counted (it represents the account), so a solo account reads 0. */
  subaccountCount: z.number().int().nonnegative(),
  /** How many topics the account has across all its sub-accounts — for the admin accounts table.
   *  Optional: only the cross-account list computes it; a single-account response omits it. */
  topicCount: z.number().int().nonnegative().optional(),
  /** Null until the account is mapped to a WorkOS organization. */
  organizationId: z.string().nullable(),
  /**
   * Whether WorkOS knows this account as an FGA resource. Nobody can be granted access to an
   * account it has never heard of, so this is what decides whether the access controls are
   * usable — deliberately NOT inferred from organizationId, which is set at creation and says
   * nothing about registration.
   */
  resourceRegistered: z.boolean(),
  /**
   * Features this account has, so the UI can hide what it hasn't bought rather than offering a
   * control that 403s. Only what's enabled — absence is the answer for everything else.
   */
  entitlements: z.array(z.string()),
  /**
   * Seat allowance — the cap on how many people a customer's admin may grant access to across this
   * account and its sub-accounts. Null = unlimited (staff-managed, and every account until a cap is
   * set). Set by staff; the grant path enforces it. Separate from entitlements: a quantity a
   * customer stays within, not a feature flag.
   */
  seats: z.number().int().nonnegative().nullable(),
  /**
   * Whether the account's topics page rolls up every sub-account's topics into one list — a view
   * preference (default true), not a capability. See the DB column of the same name.
   */
  inheritSubaccountTopics: z.boolean(),
  createdAt: z.string(),
})

export const subaccountSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  name: z.string(),
  status: accountStatusSchema,
  /** Whether WorkOS knows this sub-account as a resource — see the account field of the same name. */
  resourceRegistered: z.boolean(),
  /**
   * The one sub-account that stands in for the account itself — its home topic library. Topics are
   * cloned UP to it from a client and pushed DOWN from it to clients; nothing is edited across that
   * boundary. Exactly one sub-account per account carries this (the one created with the account).
   */
  isPrimary: z.boolean(),
  /** Seat allowance for this sub-account specifically — see accountSchema.seats. Null = unlimited. */
  seats: z.number().int().nonnegative().nullable(),
  /** Whether this sub-account's reports may carry LexisNexis full-text articles (a pull-time switch). */
  createdAt: z.string(),
})

export const createAccountSchema = z.object({
  name: z.string().min(1).max(200),
})

export const updateAccountSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    brand: accountBrandSchema.optional(),
    /** The topic roll-up preference — the one field here a customer's own admin may set. */
    inheritSubaccountTopics: z.boolean().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })

export const createSubaccountSchema = z.object({
  name: z.string().min(1).max(200),
})

export const updateSubaccountSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    status: accountStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })

export const listAccountsResponseSchema = z.object({ accounts: z.array(accountSchema) })
export const listSubaccountsResponseSchema = z.object({ subaccounts: z.array(subaccountSchema) })

/**
 * One workspace the caller can enter — a sub-account, carrying its parent account for grouping.
 * The workspace-first launcher (`GET /me/workspaces`) speaks in these: an external user sees the
 * sub-accounts they may reach, not the parent accounts they can't, so a delegate granted a single
 * client isn't stranded on an empty Accounts page. Internal admins keep the account-first launcher
 * and this returns them nothing.
 */
export const workspaceEntrySchema = z.object({
  accountId: z.uuid(),
  accountName: z.string(),
  subaccountId: z.uuid(),
  subaccountName: z.string(),
})
export const myWorkspacesResponseSchema = z.object({
  workspaces: z.array(workspaceEntrySchema),
})

export type AccountStatus = z.infer<typeof accountStatusSchema>
export type Account = z.infer<typeof accountSchema>
export type Subaccount = z.infer<typeof subaccountSchema>
export type CreateAccount = z.infer<typeof createAccountSchema>
export type UpdateAccount = z.infer<typeof updateAccountSchema>
export type CreateSubaccount = z.infer<typeof createSubaccountSchema>
export type UpdateSubaccount = z.infer<typeof updateSubaccountSchema>
export type ListAccountsResponse = z.infer<typeof listAccountsResponseSchema>
export type ListSubaccountsResponse = z.infer<typeof listSubaccountsResponseSchema>
export type WorkspaceEntry = z.infer<typeof workspaceEntrySchema>
export type MyWorkspacesResponse = z.infer<typeof myWorkspacesResponseSchema>
export type Entitlement = z.infer<typeof entitlementSchema>
export type SetEntitlement = z.infer<typeof setEntitlementSchema>
export type ListEntitlementsResponse = z.infer<typeof listEntitlementsResponseSchema>
